// =============================================================================
// api.js — Central API client layer
// =============================================================================
// ALL calls to the Express backend go through this file.
// To add a new API call: import the function you need in the page component,
// then add a new exported arrow function here following the same pattern.
// The base axios instance (`api`) automatically:
//   • Prefixes all URLs with /api
//   • Sends session cookies (withCredentials: true)
//   • Attaches the X-Access-Mode header for store/admin bypass
//   • Triggers auto-logout on 401 responses in normal mode
// =============================================================================
import axios from 'axios';

// In development the CRA proxy (setupProxy.js) forwards /api and /auth to the
// Express server, so we use relative URLs.  In production when deployed as a
// single app (backend serves the React build) relative URLs also work.
// For separate frontend/backend Azure Web Apps, set REACT_APP_API_BASE_URL.
const API_BASE = process.env.REACT_APP_API_BASE_URL || '';

// Detect store / admin access mode from the URL (mirrors App.js logic).
// This is evaluated ONCE at module load so it matches the mode for the whole session.
// store mode  → opened via HubSpot/bookmarks with hubspotOrderId / storeId in URL
// admin mode  → opened with CentralAdmin=true&code=2026 in URL
// null        → normal authenticated user
const _params = new URLSearchParams(window.location.search);
const _path = window.location.pathname;
const _accessMode = (() => {
  if (_path === '/order-editor' && _params.get('CentralAdmin') === 'true' && _params.get('code') === '2026') return 'admin';
  if (_path === '/order-editor' && _params.get('p') === 'true') return 'store'; // print mode — same bypass as store
  if (_path === '/order-editor' && (_params.get('hubspotOrderId') || _params.get('orderId') || _params.get('storeId'))) return 'store';
  return null;
})();

// Main axios instance — used for all /api/* endpoints
const api = axios.create({
  baseURL: `${API_BASE}/api`,
  headers: {
    'Content-Type': 'application/json',
    // Tell the server which access mode we are in (store/admin skip SSO check)
    ...(_accessMode ? { 'X-Access-Mode': _accessMode } : {}),
  },
  withCredentials: true, // Send/receive session cookies
});

// Auth-aware base instance (for /auth/* routes that sit outside /api)
export const authApi = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true,
});

// Intercept 401 responses — trigger auto-logout for expired sessions.
// Store/admin mode is excluded since they don't use SSO sessions.
// To add global error handling (e.g. show a toast on 500), add another interceptor here.
let _onSessionExpired = null;
export const setSessionExpiredHandler = (handler) => { _onSessionExpired = handler; };

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401 && !_accessMode) {
      console.warn('API 401 — session expired, triggering logout:', error.config?.url);
      if (_onSessionExpired) _onSessionExpired();
    }
    return Promise.reject(error);
  }
);

// =============================================================================
// Products  →  server/routes/products.js
// To add a new product endpoint, add a route there and export a function here.
// =============================================================================
export const getProducts = (params, signal) => api.get('/products', { params, signal }); // Supports search/filter/pagination params
export const getProduct = (id) => api.get(`/products/${id}`);                            // Single product detail
export const getProductStock = (id, siteId) => api.get(`/products/${id}/stock`, { params: { siteId } }); // Stock at a specific site
export const getProductSubstitutions = (id, siteId) => api.get(`/products/${id}/substitutions`, { params: { siteId } }); // Past substitution history
export const getNextAvailability = (id, siteId) => api.get(`/products/${id}/next-availability`, { params: { siteId } }); // Next expected in-stock date
export const findSubstitutes = (id, criteria) => api.post(`/products/${id}/find-substitutes`, criteria); // AI/rule substitute finder
export const getProductCategories = () => api.get('/products/categories');              // Distinct category list for filters
export const getProductAllergens = () => api.get('/products/allergens');                // All known allergen tags
export const getProductLifecycleStages = () => api.get('/products/lifecycle-stages');  // Active / discontinued / etc.
export const getProductFreeFromTags = () => api.get('/products/freefrom-tags');         // Gluten-free, dairy-free, etc.
export const getProductDietaryTags = () => api.get('/products/dietary-tags');           // Vegan, vegetarian, etc.

// =============================================================================
// Packs  →  server/routes/packs.js
// Packs are bundles of multiple products sold/ordered together.
// =============================================================================
export const getPacks = () => api.get('/packs');
export const getPack = (id) => api.get(`/packs/${id}`);
export const getPackStockCheck = (id, siteId, quantity) => api.get(`/packs/${id}/stock-check`, { params: { siteId, quantity } }); // Check if enough stock for the whole pack

// =============================================================================
// Sites  →  server/routes/sites.js
// Sites are the franchise/store locations; each order belongs to one site.
// =============================================================================
export const getSites = () => api.get('/sites');
export const getSiteStock = (id) => api.get(`/sites/${id}/stock`); // All stock levels for a site

// =============================================================================
// Orders  →  server/routes/orders.js
// Core entity — each order has lines, a site, a status, and a HubSpot deal link.
// =============================================================================
export const getOrders = () => api.get('/orders');                        // List all orders
export const getOrder = (id) => api.get(`/orders/${id}`);                 // Single order with enriched lines
export const findOrderByHubSpotOrderId = (hubspotOrderId, siteId) =>      // Look up order by HubSpotOrderId (used by print page)
  api.get(`/orders/by-hs-order/${hubspotOrderId}${siteId ? `?siteId=${siteId}` : ''}`);
export const downloadOrderPdf = (id) =>                                   // Server-generated PDF (blob) — no browser print engine
  api.get(`/orders/${id}/pdf`, { responseType: 'blob' });
export const createOrder = (data) => api.post('/orders', data);           // Create new order (body: { SiteId, CustomerName, HubSpotDealId, Notes })
export const updateOrder = (id, data) => api.put(`/orders/${id}`, data);  // Update order fields (status, notes, etc.)
export const deleteOrder = (id) => api.delete(`/orders/${id}`);           // Hard delete (use carefully)
export const getSubstitutionReasons = () => api.get('/orders/substitution-reasons'); // Dropdown options for substitution reason picker

// =============================================================================
// Order Lines  →  server/routes/orders.js (lines sub-routes)
// Each line represents one product or pack in an order.
// =============================================================================
export const addOrderLine = (orderId, data) => api.post(`/orders/${orderId}/lines`, data);                   // Add line (product or pack)
export const addPackToOrder = (orderId, packId, quantity) => api.post(`/orders/${orderId}/lines`, { PackId: packId, Quantity: quantity }); // Shorthand for adding a pack
export const updateOrderLine = (orderId, lineId, data) => api.put(`/orders/${orderId}/lines/${lineId}`, data); // Edit quantity, price, notes
export const deleteOrderLine = (orderId, lineId) => api.delete(`/orders/${orderId}/lines/${lineId}`);         // Remove a line

// Stock Check — compares order lines against site stock and flags shortfalls
export const runStockCheck = (orderId) => api.post(`/orders/${orderId}/stock-check`);

// Substitution — replace an out-of-stock product with an alternative on a line
export const substituteLineItem = (orderId, lineId, data) => api.post(`/orders/${orderId}/lines/${lineId}/substitute`, data);
// data shape: { NewProductId, Reason, Note, KeepPackCode }

// Discount — sets DiscountPct on the order header and cascades to all order lines
export const applyOrderDiscount = (orderId, discountPct) => api.post(`/orders/${orderId}/discount`, { discountPct });

// Submit — finalises the order and pushes it to HubSpot/SAP
export const submitOrder = (orderId) => api.post(`/orders/${orderId}/submit`);

// Audit — returns the change history log for an order
export const getOrderAudit = (orderId) => api.get(`/orders/${orderId}/audit`);

// =============================================================================
// Integrations  →  server/routes/integrations.js
// Manage external data connections (Pimberly PIM, On-Prem SQL, HubSpot).
// =============================================================================

// Combined status & logs (shown on the Integrations page overview cards)
export const getIntegrationStatus = () => api.get('/integrations/status');
export const getIntegrationLogs = (limit = 50) => api.get('/integrations/logs', { params: { limit } });
export const syncAll = () => api.post('/integrations/sync-all'); // Trigger all integrations in one call

// --- Pimberly PIM: pulls Products and Packs from Pimberly into the local DB ---
export const testPimberlyConnection = () => api.post('/integrations/pimberly/test');
export const syncPimberly = () => api.post('/integrations/pimberly/sync');
export const updatePimberlyConfig = (config) => api.put('/integrations/pimberly/config', config); // config: { apiKey, baseUrl, channelId }
export const previewPimberly = () => api.get('/integrations/pimberly/preview');   // See first 20 items before syncing
export const getPimberlyStatus = () => api.get('/integrations/pimberly/status');  // Last sync time and result

// --- On-Prem SQL Gateway: pulls stock levels and site data from the warehouse SQL server ---
export const testOnPremConnection = () => api.post('/integrations/onprem/test');
export const syncOnPrem = () => api.post('/integrations/onprem/sync');
export const updateOnPremConfig = (config) => api.put('/integrations/onprem/config', config); // config: { server, database, user, password, port }
export const getOnPremCustomers = () => api.get('/integrations/onprem/customers');
export const getOnPremPrices = () => api.get('/integrations/onprem/prices');
export const getOnPremStock = () => api.get('/integrations/onprem/stock');
export const getOnPremStockBySku = (sku) => api.get(`/integrations/onprem/stock/${sku}`);
export const runCustomQuery = (query) => api.post('/integrations/onprem/query', { query }); // Run ad-hoc diagnostic SQL (admin only)

// Price list (synced from on-prem, queryable with filters)
export const getPriceList = (params) => api.get('/integrations/price-list', { params });

// =============================================================================
// Users / Session  →  server/routes/users.js
// =============================================================================
export const getUsers = () => api.get('/users');
export const getUser = (id) => api.get(`/users/${id}`);
export const loginUser = (data) => api.post('/users/login', data);
export const getUserSite = (id) => api.get(`/users/${id}/site`); // Which franchise site a user belongs to

// =============================================================================
// HubSpot CRM  →  server/routes/hubspot.js
// Manages the link between orders and HubSpot deals/line items.
// =============================================================================
export const hubspotLaunch = (data) => api.post('/hubspot/launch', data);           // Start a HubSpot workflow
export const hubspotTestConnection = () => api.post('/hubspot/test');               // Verify the API key works
export const hubspotGetDeal = (dealId) => api.get(`/hubspot/deal/${dealId}`);       // Fetch deal metadata from HubSpot
export const hubspotImportOrder = (orderId, dealId) => api.post(`/hubspot/import-order/${orderId}`, dealId ? { dealId } : {}); // Pull HubSpot line items into local order
export const hubspotSubmitOrder = (orderId) => api.post(`/hubspot/submit-order/${orderId}`); // Push completed order back to HubSpot
export const hubspotGetLogs = (limit) => api.get('/hubspot/logs', { params: { limit } }); // Recent HubSpot API call log
export const hubspotPreviewLines = (orderId) => api.get(`/hubspot/preview-lines/${orderId}`); // Preview what will be pushed before confirming
export const hubspotPushLines = (orderId, dealId) => api.post(`/hubspot/push-lines/${orderId}`, { dealId }); // Push order lines to HubSpot deal

// =============================================================================
// Schedules  →  server/routes/schedules.js
// Automated background tasks (e.g. nightly Pimberly sync, stock refresh).
// To add a new scheduled task: add a TaskKey in server/scheduler.js and insert
// a row in the Schedules table via a migration.
// =============================================================================
export const getSchedules = () => api.get('/schedules');                                       // List all schedules and their status
export const updateSchedule = (id, data) => api.put(`/schedules/${id}`, data);                // Toggle enabled or change interval
export const runScheduleNow = (id) => api.post(`/schedules/${id}/run`);                       // Manually trigger a schedule
export const getScheduleHistory = (id, limit) => api.get(`/schedules/${id}/history`, { params: { limit } }); // Past run results
export const getRecentScheduleLogs = (limit) => api.get('/schedules/logs/recent', { params: { limit } }); // Combined recent log for all schedules

// =============================================================================
// Analytics  →  server/routes/analytics.js
// Data for the Dashboard page charts and stats.
// To add a new chart: add an endpoint in server/routes/analytics.js and
// export a function here, then consume it in DashboardPage.js.
// =============================================================================
export const getAnalyticsOverview = (params) => api.get('/analytics/overview', { params }); // KPI totals (orders, value, subs)
export const getAnalyticsSites = (params) => api.get('/analytics/sites', { params });       // Per-site breakdown table
export const getAnalyticsOrderRate = (params) => api.get('/analytics/order-rate', { params }); // Time-series data for trend chart
export const getAnalyticsLogs = (params) => api.get('/analytics/logs', { params });         // Filterable activity log

export default api;
